import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Edit, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { useCreateApplicationAuth } from './useCreateApplicationAuth'

type EditCredentialsProps = {
  authId: string
  applicationId: string
  clientKey: string
  newPassword: string
  subId: string | null
  headers: object
  metadata: object | null
}

export function useEditApplicationAuthCredentials() {
  const { t } = useTranslation('sso')
  const { handleCreateAuth } = useCreateApplicationAuth()

  const invalidateAuthData = () => {
    queryClient.invalidateQueries({ queryKey: ['/application-auth/findOne'] })
  }

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    EditCredentialsProps
  >({
    mutationKey: ['/application-auth/edit-credentials'],
    mutationFn: async (data) => {
      await api.backend.delete(`/application-auth/${data.authId}`)
      try {
        return await handleCreateAuth({
          applicationId: data.applicationId,
          subId: data.subId,
          clientKey: data.clientKey,
          secretData: {
            password: data.newPassword,
          },
          headers: data.headers,
          metadata: data.metadata,
        })
      } catch (error) {
        invalidateAuthData()
        throw error
      }
    },
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Edit,
          title: t(
            'applications.drawer_manage_auth.toast_edit_credentials_success.title',
          ),
        },
      })
      invalidateAuthData()
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t(
            'applications.drawer_manage_auth.toast_edit_credentials_error.title',
          ),
          description:
            error?.response?.data?.friendlyMessage ||
            t(
              'applications.drawer_manage_auth.toast_edit_credentials_error.description',
            ),
        },
      })
      invalidateAuthData()
    },
  })

  const handleEditCredentials = async (data: EditCredentialsProps) => {
    return mutateAsync(data)
  }

  return {
    handleEditCredentials,
    isEditingCredentials: isPending,
  }
}
